import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import moment from 'moment'

import type Employee from 'modules/employees/store/models/Employee'
import PolicyAssignments from 'modules/timeoff/components/Leaves/PolicyAssignments'
import { hasPolicyTemporality } from 'modules/timeoff/lib/features/TimeoffFeatures'
import { type AllowanceIncidences as AllowanceIncidencesType } from 'modules/timeoff/store/collections/allowanceIncidences'
import { type Allowances } from 'modules/timeoff/store/collections/allowances'
import { type TimeoffPolicies } from 'modules/timeoff/store/collections/timeoffPolicies/policies'
import { type PolicyTimelines } from 'modules/timeoff/store/collections/timeoffPolicies/policyTimelines'
import type { PolicyTimelineItem } from 'modules/timeoff/store/models/TimeoffPolicy/PolicyTimeline'

import AllowanceIncidences from './AllowanceIncidences'
import Calendar from './Calendar'
import LeavesList from './LeavesList'
import Overview from './Overview'
import * as styles from './index.vanilla.css'

interface Props {
  employee: Employee
  canCreateLeaves: boolean
  canApproveLeaves: boolean
  hasManualAdjustmentOptions: boolean
  allowances: Allowances
  allowanceIncidences: AllowanceIncidencesType
  policies: TimeoffPolicies
  policyTimelines: PolicyTimelines
}

// Memoize Leaves to avoid unnecessary re-renders when props do not change
const Leaves = React.memo(function Leaves({
  employee,
  canCreateLeaves,
  canApproveLeaves,
  hasManualAdjustmentOptions,
  allowances,
  allowanceIncidences,
  policies,
  policyTimelines,
}: Props) {
  const year = moment().year()
  const [selectedTimelineItem, setSelectedTimelineItem] = React.useState<PolicyTimelineItem>(
    policyTimelines?.timeline?.current
  )

  return (
    <Box flexDirection="row" flexGrow>
      <Box className={styles.column} gap="s32" paddingRight="s32">
        {hasPolicyTemporality() && (
          <PolicyAssignments
            employee={employee}
            selectedTimelineItem={selectedTimelineItem}
            setSelectedTimelineItem={setSelectedTimelineItem}
            policies={policies}
            policyTimelines={policyTimelines}
          />
        )}
        <Overview
          employee={employee}
          policyTimelineItem={selectedTimelineItem}
          allowances={allowances}
          allowanceIncidences={allowanceIncidences}
          policies={policies}
        />
        <LeavesList employee={employee} year={year} />
      </Box>
      <Box className={styles.calendar}>
        <Calendar
          employee={employee}
          year={year}
          canCreateLeaves={canCreateLeaves}
          canApproveLeaves={canApproveLeaves}
        />
        <AllowanceIncidences
          employee={employee}
          timelineItem={selectedTimelineItem}
          hasManualAdjustmentOptions={hasManualAdjustmentOptions}
          allowanceIncidences={allowanceIncidences}
          allowances={allowances}
        />
      </Box>
    </Box>
  )
})

// Set displayName for better debugging in React DevTools and error messages
Leaves.displayName = 'Leaves'

export default Leaves
